<template>
  <Dialog
    v-model:visible="visible"
    modal
    class="p-dialog--wizard"
    :closable="false"
    :close-on-escape="false"
  >
    <template #header>
      <div class="modal-header">
        <div class="modal-header__text">
          <div class="modal-header__title">
            <slot name="title"></slot>
          </div>
          <div class="modal-header__description">
            <slot name="description"></slot>
          </div>
        </div>

        <Button @click="cancelCreate()" class="p-dialog-wizard--close-button">
          <IconModalClose />
        </Button>
      </div>
    </template>

    <LoadingModal v-if="loading" />

    <div class="modal-body modal-body--wizard" v-if="!loading">
      <ol class="modal-nav">
        <li
          v-for="step in steps"
          :key="step.stepNumber"
          class="modal-step"
          :class="{
            'modal-step--active': activeStep === step.stepNumber,
            'modal-step--error': activeStep > step.stepNumber && !step.stepValid,
            'modal-step--complete': activeStep > step.stepNumber && step.stepValid,
          }"
          @click="stepChanged(step.stepNumber)"
        >
          <IconStepCheck class="icon-check" v-if="activeStep > step.stepNumber && step.stepValid" />
          <IconStepExclaim
            class="icon-exclaim"
            v-if="activeStep > step.stepNumber && !step.stepValid"
          />
          <div>
            <span>{{ step.stepName }}</span>
            {{ step.stepDescription }}
          </div>
        </li>
      </ol>

      <div class="modal-content-frame">
        <slot name="body"></slot>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        class="cancel-button"
        severity="contrast"
        @click="cancelCreate()"
        outlined
        autofocus
      />

      <Button
        :disabled="loading || !canGoBack"
        label="Back"
        severity="contrast"
        @click="prevStage()"
        outlined
        autofocus
      />

      <template v-for="(step, index) in steps">
        <Button
          v-if="activeStep === step.stepNumber && index < steps.length && activeStep < steps.length"
          :key="`step-${index}`"
          :disabled="loading || !step.stepValid || submitLoading"
          :class="{ 'submit-loading': submitLoading }"
          label="Next"
          severity="secondary"
          @click="nextStage()"
          autofocus
        />
      </template>

      <Button
        v-if="activeStep === steps.length"
        :disabled="loading || !allValid || submitLoading"
        :class="{ 'submit-loading': submitLoading }"
        label="Submit"
        severity="secondary"
        @click="saveModal()"
        autofocus
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Button, Dialog } from 'primevue';

import LoadingModal from '../global/LoadingModal.vue';

import IconModalClose from '@/icons/modal/ModalClose.svg?component';
import IconStepCheck from '@/icons/events/IconStepCheck.svg?component';
import IconStepExclaim from '@/icons/events/IconStepExclaim.svg?component';

import type { WizardStep } from '@/types/Wizard';

import { useConfirm } from 'primevue/useconfirm';
const confirm = useConfirm();

const props = defineProps<{
  activeStep: number;
  steps: WizardStep[];
  loading: boolean;
  submitLoading: boolean;
}>();

const visible = defineModel<boolean>('visible');

const emit = defineEmits(['onStepChanged', 'onCancel', 'onSubmit']);

const canGoBack = computed(() => {
  return props.activeStep != 1;
});

const allValid = computed(() => {
  let valid = true;

  props.steps.forEach((step) => {
    if (!step.stepValid) {
      valid = false;
    }
  });

  return valid;
});

const cancelCreate = () => {
  confirm.require({
    message:
      'It looks like you have unsaved changes. If you leave now, all your progress will be lost. ',
    header: 'Are you sure you want to leave?',
    icon: 'pi pi-exclamation-triangle',
    group: 'wizard-modal',
    rejectProps: {
      label: 'Close',
    },
    acceptProps: {
      label: 'Leave',
    },
    accept: async () => {
      emit('onCancel');
      visible.value = false;
    },
    reject: () => {},
  });
};

const nextStage = () => {
  stepChanged(props.activeStep + 1);
};

const prevStage = () => {
  stepChanged(props.activeStep - 1);
};

const stepChanged = (stepNumber: number) => {
  emit('onStepChanged', stepNumber);
};

const saveModal = () => {
  emit('onSubmit');
};
</script>
